import imagesLoaded from 'imagesloaded';
import React, { useState, Fragment, useContext, useCallback } from 'react';
import { flushSync } from 'react-dom';
import Modal from 'react-modal';

// components
import MediaModal from './components/MediaModal';
import GlossaryPage from './pages/GlossaryPage';
import Intro from './pages/Intro';
import SelectPage from './pages/SelectPage';
import SlidesPage from './pages/SlidesPage';

// functionality
import { fadeIn, fadeOut } from './animations/pageAnimation';
import ModalContext from './Context/modal';
import useAppMeta from './hooks/useAppMeta';
import useFetch from './hooks/useFetch';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import useInitRoute from './hooks/useInitRoute';

Modal.setAppElement('#root');

const App = ({ route }) => {
  const rootElem = document.querySelector('#root');
  const [step, setStep] = useState(null);
  const [personData, setPersonData] = useState([]);
  // Set by "Start over" so SlidesPage begins at slide 1 explicitly, rather
  // than deriving it from the URL: navigate() and this remount happen in the
  // same tick, and react-router's own context update isn't guaranteed to be
  // visible to SlidesPage by the time it re-mounts and reads useParams().
  const [restartSlides, setRestartSlides] = useState(false);
  const { showMediaModal } = useContext(ModalContext);
  useGoogleAnalytics();

  // prep loading
  const [isLoading, setIsLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);

  // slides animation
  const [slideIsAnimating, setSlideIsAnimating] = useState(true);
  const [introIsAnimating, setIntroIsAnimating] = useState(true);

  const readyToFadeIn = () => {
    const begin = () => setIsLoading(false);
    const complete = () => setPageReady(true);
    fadeIn(begin, complete);
  };

  const goToStep = useCallback((step, restart) => {
    const complete = () => {
      // Deferred to a macrotask: goToStep can be called from inside a
      // useEffect (useInitRoute) as well as from real click handlers, and
      // flushSync throws/warns ("called from inside a lifecycle method")
      // if invoked while React is still processing an effect. Deferring
      // guarantees flushSync always runs outside of any React work loop,
      // regardless of caller.
      setTimeout(() => {
        // flushSync forces the '' step to actually commit before setting the
        // real step. Without it, React batches both calls into one update -
        // if the target step is the same as the current one (e.g. "Start
        // over" while already on 'slides'), the net change is a no-op and
        // the page never remounts to reset its state.
        flushSync(() => {
          setPageReady(false);
          setStep(''); // unset for state update - helpful to just re-init same step
        });
        // flushSync again so the destination step is actually committed to the DOM
        // before imagesLoaded runs below - otherwise React 18+'s automatic batching
        // defers this update and imagesLoaded queries stale/empty '.page' elements.
        flushSync(() => {
          setRestartSlides(!!restart);
          setStep(step);
          setIsLoading(true);
        });
        imagesLoaded('.page', () => {
          readyToFadeIn();
          // Consume the flag after SlidesPage has mounted and read it, so a
          // later, unrelated remount doesn't unexpectedly restart at slide 1.
          setRestartSlides(false);
        });
      }, 0);
    };

    fadeOut(complete);
  }, []);

  // custom hooks
  const { data } = useFetch(rootElem.getAttribute('data-api-url'));
  const { appMeta } = useAppMeta(data);

  const setPersonDataStable = useCallback((data) => {
    setPersonData(data);
  }, []);

  useInitRoute(data, setPersonDataStable, goToStep);

  if (!data) {
    return (
      <div className="loadingApp">
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <Fragment>
      {isLoading && route !== 'glossary' ? (
        <div className="loadingApp">
          <span>Loading...</span>
        </div>
      ) : null}

      <div className="loadScreen" />

      <div className={`App ${showMediaModal ? 'modalOpen' : ''}`}>
        {route === 'glossary' ? (
          <GlossaryPage glossaryData={data.glossary.data} glossaryPdf={data.glossary_pdf} />
        ) : (
          <>
            {step === 'intro' ? (
              <Intro
                data={data}
                goToSelect={() => goToStep('personSelect')}
                introIsAnimating={introIsAnimating}
                setIntroIsAnimating={setIntroIsAnimating}
              />
            ) : null}

            {step === 'personSelect' ? (
              <SelectPage
                appMeta={appMeta}
                stories={data.stories.data}
                personData={personData}
                setPersonData={setPersonData}
                goToSlides={() => goToStep('slides')}
              />
            ) : null}

            {step === 'slides' ? (
              <SlidesPage
                appMeta={appMeta}
                timelineData={data.maptimelines.data}
                pageReady={pageReady}
                personData={personData}
                slides={personData.slides.data}
                goToStep={goToStep}
                slideIsAnimating={slideIsAnimating}
                setSlideIsAnimating={setSlideIsAnimating}
                startAtBeginning={restartSlides}
              />
            ) : null}
          </>
        )}
      </div>

      <MediaModal detailPages={data.detailPages.data} />
    </Fragment>
  );
};

export default App;
